import React, {useEffect} from "react";
import { useTranslation } from "react-i18next";

import { SFHeroContainer, SFHeroSlide, SFIcons } from "@sf-core";
import { HERO_TYPES } from "../../constants/heroTypes";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAcademyUrl,
  getCourseUrl,
  getProfileUrl,
} from "../../constants/urls";
import { getAcademyIcon } from "../../constants/academyIcons";
import { getNextCourse } from "../../services/domainService";
import { STATUS } from "../../constants/statuses";
import { saveCourseTrack } from "../../services/courseService";
import SFProgressHero from "./progressHero";
import { useDispatch } from "react-redux";
import { userConfigurationActions } from "../../redux/reducers/userConfigurationReducer";

const HeroesCarousel = ({ items = [] }) => (
  <SFHeroContainer>
    {items.map((item, index) => {
      switch (item.type) {
      case HERO_TYPES.Profile:
        return <ProfileHero key={index} hero={item} />;
      case HERO_TYPES.Welcome:
        return <WelcomeHero key={index} hero={item} />;
      case HERO_TYPES.Progress:
        return <SFProgressHero key={index} hero={item} />;
      case HERO_TYPES.Academy:
        return <AcademyHero key={index} hero={item} />;
      case HERO_TYPES.Custom:
        return <CustomHero key={index} hero={item} />;
      default:
        return null;
      }
    })}
  </SFHeroContainer>
);

const ProfileHero = ({ hero }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      userConfigurationActions.setState({
        firstName: hero.username,
      })
    );
  }, [hero]);

  // Status Not Started when the hero status is null
  let profileStatus = STATUS.NOT_STARTED;
  // Status In Progress when the hero status is 0, 1, 2 or 3
  if ([0, 1, 2, 3].includes(hero.status)) profileStatus = STATUS.IN_PROGRESS;
  // Status Completed when the hero status is 4 (last step)
  if (hero.status === 4) profileStatus = STATUS.COMPLETED;

  const getHeroData = () => {
    switch (profileStatus) {
    case STATUS.NOT_STARTED:
      return {
        description: t("homeScreen.heroes.profileNotStarted.description"),
        actions: [
          {
            label: t("homeScreen.heroes.profileNotStarted.button"),
            onClick: () => navigate(getProfileUrl()),
          },
        ],
      };
    case STATUS.IN_PROGRESS:
      return {
        description: t("homeScreen.heroes.profileInProgress.description"),
        actions: [
          {
            label: t("homeScreen.heroes.profileInProgress.button"),
            variant: "secondary",
            icon: <SFIcons.SFPlayIcon size="large" />,
            onClick: () => navigate(getProfileUrl()),
          },
        ],
      };
    case STATUS.COMPLETED:
    default:
      return {
        description: t("homeScreen.heroes.profileCompleted.description"),
        actions: [
          {
            label: t("homeScreen.heroes.profileCompleted.button"),
            onClick: () => navigate(getProfileUrl()),
          },
        ],
      };
    }
  };

  let { description, actions } = getHeroData();

  return (
    <SFHeroSlide
      title={t("homeScreen.heroes.welcomeTitle", { value: hero.username })}
      description={description}
      image={hero.image}
      actions={actions}
    />
  );
};

const WelcomeHero = ({ hero }) => {
  const { t } = useTranslation();

  return (
    <SFHeroSlide
      title={t("homeScreen.heroes.welcomeTitle", { value: hero.username })}
      description={t("homeScreen.heroes.welcomeMessage")}
      image={hero.image}
      stats={[
        {
          label: t("homeScreen.heroes.academiesLabel"),
          total: hero.academies,
        },
        {
          label: t("homeScreen.heroes.skillPathsLabel"),
          total: hero.skillPaths,
        },
        {
          label: t("homeScreen.heroes.coursesLabel"),
          total: hero.courses,
        },
      ]}
    />
  );
};

const AcademyHero = ({ hero }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <SFHeroSlide
      title={hero.academyName}
      description={hero.academyDescription}
      image={hero.image}
      thumbnail={getAcademyIcon(hero.thumbnail)}
      stats={
        hero.status === STATUS.IN_PROGRESS
          ? [
            {
              type: "progress",
              title: t("homeScreen.heroes.coursesCompletedLabel", {
                completed: hero.completedCourses,
                total: hero.courses,
              }),
              value: (hero.completedDuration / hero.totalDuration) * 100,
              maxWidth: 340,
            },
          ]
          : [
            {
              label: t("homeScreen.heroes.skillPathsLabel"),
              total: hero.skillPaths,
            },
            {
              label: t("homeScreen.heroes.coursesLabel"),
              total: hero.courses,
            },
          ]
      }
      actions={
        hero.status === STATUS.IN_PROGRESS || hero.status === STATUS.COMPLETED
          ? [
            ...(hero.status === STATUS.IN_PROGRESS
              ? [
                {
                  label: t("homeScreen.heroes.resumeButtonLabel"),
                  variant: "secondary",
                  icon: <SFIcons.SFPlayIcon size="large" />,
                  onClick: () => {
                    getNextCourse(hero.academyId).then((res) => {
                      // Request to track the skillPath id from where the course was accessed
                      let data = {
                        skillPathId: res.data.skillPathId,
                        contentId: res.data.id,
                      };
                      saveCourseTrack(data);
                      navigate(getCourseUrl(res.data.id), {
                        state: { previousLoc: location.pathname },
                      });
                    });
                  },
                },
              ]
              : []),
            {
              label: t("homeScreen.heroes.viewDetailsButtonLabel"),
              onClick: () => navigate(getAcademyUrl(hero.academyId)),
            },
          ]
          : [
            {
              label: t("homeScreen.heroes.getStartedLabel"),
              onClick: () => navigate(getAcademyUrl(hero.academyId)),
            },
          ]
      }
    />
  );
};

const CustomHero = ({ hero }) => {
  const navigate = useNavigate();

  const navigateTo = (action) => {
    if (action.sameWindow) {
      if (action.url.startsWith("/")) {
        navigate(action.url);
      } else {
         
        window.location.href = action.url;
      }
    } else if (action.newWindow) {
       
      window.open(action.url);
    } else {
      if (action.url.startsWith("/")) {
        navigate(action.url);
      } else {
         
        window.open(action.url);
      }
    }
  };

  return (
    <SFHeroSlide
      title={hero.title}
      description={hero.description}
      image={hero.image}
      actions={
        hero.actions.length > 0 &&
        hero.actions.map((action) => ({
          ...action,
          onClick: () => {
            navigateTo(action);
          },
        }))
      }
    />
  );
};

export default HeroesCarousel;
